import { useCookieManager } from './useCookieManager';
/**
 * Checkout-Prefill: speichert Kontakt- + Adressfelder langfristig in einem
 * First-Party-Cookie, damit der Kunde bei einer erneuten Bestellung schnell
 * vorausgefüllte Felder hat.
 *
 * - Gespeichert wird beim Abschicken einer Bestellung.
 * - Vorausgefüllt wird beim Laden des Checkouts (nur leere Felder).
 * - DSGVO: Das Cookie ist ein KOMFORT-/funktionales Cookie und wird nur
 *   geschrieben/gelesen, wenn die Einwilligung für „functional" vorliegt
 *   (siehe useCookieManager / CookieConsent).
 *
 * Bewusst getrennt vom Warenkorb-localStorage (das beim Abschluss geleert wird),
 * damit die Daten eine Bestellung „überleben".
 */
const COOKIE_NAME = 'checkout_prefill';
const VALID_DAYS = 365;
const MAX_AGE_MS = VALID_DAYS * 24 * 60 * 60 * 1000;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const str = (value) => (typeof value === 'string' ? value.trim() : '');
export function useCheckoutPrefill() {
    const cookies = useCookieManager();
    /** Komfort-/funktionale Cookies erlaubt? (DSGVO-Einwilligung) */
    const canPersist = () => {
        try {
            return cookies.isCategoryEnabled('functional');
        }
        catch {
            return false;
        }
    };
    /** Liest ein gültiges Prefill-Cookie (oder null). */
    const load = () => {
        if (!canPersist())
            return null;
        try {
            const raw = cookies.getCookie(COOKIE_NAME);
            if (!raw)
                return null;
            const data = JSON.parse(decodeURIComponent(raw));
            if (!data || typeof data !== 'object')
                return null;
            if (!data.customer || !data.address)
                return null;
            // Abgelaufen?
            if (!Number.isFinite(data.savedAt) || Date.now() - Number(data.savedAt) > MAX_AGE_MS)
                return null;
            return {
                customer: {
                    name: str(data.customer.name),
                    phone: str(data.customer.phone),
                    email: str(data.customer.email)
                },
                address: {
                    street: str(data.address.street),
                    city: str(data.address.city),
                    postalCode: str(data.address.postalCode)
                },
                savedAt: Number(data.savedAt)
            };
        }
        catch {
            return null;
        }
    };
    /** Hat valide, brauchbare Prefill-Daten (mind. ein Kontaktfeld)? */
    const hasValidPrefill = () => {
        const pf = load();
        return Boolean(pf && (pf.customer.name || pf.customer.email || pf.customer.phone));
    };
    /** Speichert Kontakt-/Adressfelder (beim Abschicken der Bestellung). */
    const save = (customer, address) => {
        if (!canPersist())
            return;
        const payload = {
            customer: {
                name: str(customer?.name),
                phone: str(customer?.phone),
                email: str(customer?.email)
            },
            address: {
                street: str(address?.street),
                city: str(address?.city),
                postalCode: str(address?.postalCode)
            },
            savedAt: Date.now()
        };
        // Nur speichern, wenn überhaupt brauchbare Kontaktdaten vorhanden sind.
        if (!payload.customer.name && !payload.customer.email && !payload.customer.phone)
            return;
        try {
            cookies.setCookie(COOKIE_NAME, encodeURIComponent(JSON.stringify(payload)), VALID_DAYS);
        }
        catch {
            /* Cookie konnte nicht gesetzt werden – ignorieren. */
        }
    };
    /** Entfernt das Prefill-Cookie. */
    const clear = () => {
        try {
            cookies.deleteCookie(COOKIE_NAME);
        }
        catch {
            /* ignore */
        }
    };
    /**
     * Füllt ein Ziel-Objekt (Checkout-Formular) mit den gespeicherten Werten –
     * aber nur LEERE Felder, damit bereits eingegebene/aus dem Warenkorb
     * wiederhergestellte Daten nicht überschrieben werden.
     * Liefert true, wenn mindestens ein Feld vorausgefüllt wurde.
     */
    const applyToForm = (form) => {
        const pf = load();
        if (!pf)
            return false;
        let filled = false;
        const fill = (current, next, set) => {
            if (!str(current) && next) {
                set(next);
                filled = true;
            }
        };
        fill(form.name, pf.customer.name, (v) => { form.name = v; });
        fill(form.phone, pf.customer.phone, (v) => { form.phone = v; });
        fill(form.email, pf.customer.email, (v) => { form.email = v; });
        if (form.address) {
            fill(form.address.street, pf.address.street, (v) => { form.address.street = v; });
            fill(form.address.city, pf.address.city, (v) => { form.address.city = v; });
            fill(form.address.postalCode, pf.address.postalCode, (v) => { form.address.postalCode = v; });
        }
        return filled;
    };
    const isValidEmail = (email) => EMAIL_RE.test(str(email));
    return { canPersist, load, save, clear, hasValidPrefill, applyToForm, isValidEmail };
}
